import { useParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, MONTHS } from "../api";
import type { ChildRule, SubmissionDetail as Detail, SyncResult } from "../types";
import { Icon } from "../components/Icon";
import { ConfirmDialog } from "../components/Modal";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, SkeletonRows } from "../components/ui";

const STATUS_TONE: Record<string, string> = {
  synced: "synced",
  pending: "pending",
  unmatched: "unmatched",
  error: "error",
};

export function SubmissionDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const [preview, setPreview] = useState<SyncResult | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const detail = useQuery({
    queryKey: ["submission", id],
    queryFn: () => api.get<Detail>(`/submissions/${id}`),
  });
  const rules = useQuery({
    queryKey: ["rules"],
    queryFn: () => api.get<{ rules: ChildRule[] }>("/rules"),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["submission", id] });
    qc.invalidateQueries({ queryKey: ["submissions"] });
  };

  const rematch = useMutation({
    mutationFn: (ruleId: number | null) =>
      api.post(`/submissions/${id}/rematch`, { child_rule_id: ruleId }),
    onSuccess: invalidate,
    onError: (e) => toast.error((e as Error).message),
  });
  const setOverride = useMutation({
    mutationFn: (v: { date: string; dish: string | null }) =>
      api.put(`/submissions/${id}/days/${v.date}`, { dish_override: v.dish }),
    onSuccess: invalidate,
    onError: (e) => toast.error((e as Error).message),
  });
  const doPreview = useMutation({
    mutationFn: () => api.post<SyncResult>(`/submissions/${id}/preview`),
    onSuccess: (r) => setPreview(r),
    onError: (e) => toast.error((e as Error).message),
  });
  const doSync = useMutation({
    mutationFn: () => api.post<SyncResult>(`/submissions/${id}/sync`),
    onSuccess: (r) => {
      setPreview(null);
      toast.success(
        `${r.counts.insert} added · ${r.counts.update} updated · ${r.counts.delete} removed`,
      );
      invalidate();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const doDelete = useMutation({
    mutationFn: () => api.del<{ removed: number }>(`/submissions/${id}/events`),
    onSuccess: (r) => {
      setConfirmDelete(false);
      toast.success(`Removed ${r.removed} calendar event${r.removed === 1 ? "" : "s"}`);
      invalidate();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  if (detail.isLoading) return <SkeletonRows n={4} />;
  if (detail.error)
    return (
      <div className="toast error">
        <Icon name="alert-circle" size={18} />
        <span>{(detail.error as Error).message}</span>
      </div>
    );

  const d = detail.data!;
  const s = d.submission;

  return (
    <>
      <Link
        to="/"
        className="cluster small muted"
        style={{ marginBottom: 14, display: "inline-flex" }}
      >
        <Icon name="arrow-left" size={15} /> Dashboard
      </Link>
      <h1>
        {s.student_first} {s.student_last}
      </h1>
      <p className="muted" style={{ margin: "4px 0 20px" }}>
        {MONTHS[s.month - 1]} {s.year} order
      </p>

      <Card>
        <dl className="kv">
          <dt>Status</dt>
          <dd>
            <Badge tone={STATUS_TONE[s.status] ?? "info"} dot>
              {s.status}
            </Badge>
            {s.status_detail && (
              <span className="muted small"> · {s.status_detail}</span>
            )}
          </dd>
          <dt>Child rule</dt>
          <dd>
            <select
              style={{ maxWidth: 320 }}
              value={s.child_rule_id ?? ""}
              onChange={(e) =>
                rematch.mutate(e.target.value ? Number(e.target.value) : null)
              }
            >
              <option value="">— unmatched —</option>
              {(rules.data?.rules ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label || r.match_first_name} → {r.calendar_name}
                </option>
              ))}
            </select>
          </dd>
          <dt>Menu</dt>
          <dd>
            {d.hasMenu ? (
              <Badge tone="synced" dot>
                loaded
              </Badge>
            ) : (
              <span className="muted small">
                none — titles fall back to the choice name
              </span>
            )}
          </dd>
          {s.edit_url && (
            <>
              <dt>Form</dt>
              <dd>
                <a href={s.edit_url} target="_blank" rel="noreferrer">
                  Open the Google Form response{" "}
                  <Icon name="external" size={13} />
                </a>
              </dd>
            </>
          )}
        </dl>
      </Card>

      <Card
        title="Days"
        sub={`${d.days.filter((x) => x.buying).length} buying day${
          d.days.filter((x) => x.buying).length === 1 ? "" : "s"
        }. One calendar event per buying day — tap a dish to change the event title.`}
      >
        <div className="day-list">
          {d.days.map((day) => {
            const when = new Date(
              `${day.date}T00:00:00Z`,
            ).toLocaleDateString("en-US", {
              weekday: "short",
              month: "short",
              day: "numeric",
              timeZone: "UTC",
            });
            if (!day.buying) {
              return (
                <div key={day.date} className="day-item noserve">
                  <span className="day-when">{when}</span>
                  <span className="muted small">
                    {day.choice === "No Lunch" || !day.choice
                      ? "No lunch"
                      : day.choice}
                  </span>
                </div>
              );
            }
            return (
              <div key={day.date} className="day-item buying">
                <span className="day-when">{when}</span>
                <div className="day-body">
                  <Badge tone="pending">
                    {day.choice}
                    {day.extra_slice ? " + extra slice" : ""}
                  </Badge>
                  <DishCell
                    value={day.dish_override ?? day.resolvedDish ?? ""}
                    placeholder={day.displayDish ?? ""}
                    overridden={!!day.dish_override}
                    onSave={(dish) =>
                      setOverride.mutate({ date: day.date, dish })
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card title="Calendar sync">
        <div className="actions">
          <Button
            variant="secondary"
            icon="calendar"
            disabled={!s.child_rule_id}
            loading={doPreview.isPending}
            onClick={() => doPreview.mutate()}
          >
            Preview
          </Button>
          <Button
            icon="refresh"
            disabled={!s.child_rule_id}
            loading={doSync.isPending}
            onClick={() => doSync.mutate()}
          >
            Sync now
          </Button>
          <Button
            variant="danger"
            icon="trash"
            disabled={d.events.length === 0}
            onClick={() => setConfirmDelete(true)}
          >
            Remove all events
          </Button>
        </div>

        {preview && (
          <>
            <div className="divider" />
            <p className="small muted" style={{ marginBottom: 8 }}>
              Preview — {preview.counts.insert} add, {preview.counts.update}{" "}
              update, {preview.counts.delete} remove
            </p>
            <div className="day-list">
              {preview.items.length === 0 && (
                <p className="muted small">Nothing to change.</p>
              )}
              {preview.items.map((it) => (
                <div key={it.date + it.action} className="day-item buying">
                  <span className="day-when mono">{it.date.slice(5)}</span>
                  <div className="day-body">
                    <Badge
                      tone={
                        it.action === "insert"
                          ? "synced"
                          : it.action === "delete"
                            ? "error"
                            : it.action === "update"
                              ? "pending"
                              : "info"
                      }
                    >
                      {it.action}
                    </Badge>
                    <span>{it.summary}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {d.events.length > 0 && (
          <>
            <div className="divider" />
            <p className="small muted" style={{ marginBottom: 8 }}>
              {d.events.length} calendar event
              {d.events.length === 1 ? "" : "s"} created
            </p>
            <div className="day-list">
              {d.events.map((e) => (
                <div key={e.id} className="day-item buying">
                  <span className="day-when mono">{e.date.slice(5)}</span>
                  <div className="day-body">
                    <span className="grow">{e.summary}</span>
                    <span className="faint small">
                      {e.last_synced_at
                        ? new Date(e.last_synced_at).toLocaleDateString()
                        : "—"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

      </Card>

      {confirmDelete && (
        <ConfirmDialog
          title="Remove all calendar events?"
          body={`This deletes the ${d.events.length} event(s) this app created for this order. The order itself stays; you can re-sync later.`}
          confirmLabel="Remove events"
          danger
          loading={doDelete.isPending}
          onConfirm={() => doDelete.mutate()}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </>
  );
}

function DishCell({
  value,
  placeholder,
  overridden,
  onSave,
}: {
  value: string;
  placeholder: string;
  overridden: boolean;
  onSave: (dish: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value);

  if (!editing) {
    return (
      <span
        className="inline-edit"
        onClick={() => {
          setV(value);
          setEditing(true);
        }}
      >
        {value || <span className="faint">{placeholder || "set dish"}</span>}
        <Icon name="pencil" size={13} className="pencil" />
      </span>
    );
  }
  return (
    <span className="cluster">
      <input
        autoFocus
        type="text"
        value={v}
        placeholder={placeholder}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            onSave(v.trim() || null);
            setEditing(false);
          }
          if (e.key === "Escape") setEditing(false);
        }}
        style={{ minWidth: 180 }}
      />
      <Button
        size="sm"
        onClick={() => {
          onSave(v.trim() || null);
          setEditing(false);
        }}
      >
        Save
      </Button>
      {overridden && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            onSave(null);
            setEditing(false);
          }}
        >
          Reset
        </Button>
      )}
    </span>
  );
}
